import {
  CopyOutlined,
  DollarOutlined,
  EditOutlined,
  LinkOutlined,
  ReloadOutlined,
  WhatsAppOutlined,
} from '@ant-design/icons';
import { TextField } from '@refinedev/antd';
import { Alert, Button, Flex, Form, Input, Modal, Skeleton, Typography } from 'antd';
import useNotification from 'antd/es/notification/useNotification';
import dayjs from 'dayjs';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import LinkTextField from '@/components/general/LinkTextField';
import {
  ModalFormActions,
  ShowInfoGrid,
  ShowInfoItem,
} from '@/components/general/ShowInfo/ShowInfo';
import { ShowSection, ShowSubsection } from '@/components/general/ShowInfo/ShowSection';
import { TECHNICIAN_URLS } from '@/constants/urls';
import { useCountryContext } from '@/contexts/CountryContext';
import { usePermissionContext } from '@/contexts/PermissionContext';
import { useSelectScrollSearch } from '@/hooks/useSelectScrollSearch';
import {
  getPaymentMethodFieldsByType,
  PaymentMethodField,
  PaymentMethodTypeLabels,
} from '@/model/General/PaymentMethod';
import { Technician } from '@/model/General/Technician';
import {
  SERVICE_ORDER_STATUS,
  SERVICE_ORDER_TYPE,
  ServiceOrder,
  ServiceOrderTypeString,
} from '@/model/ServiceOrder/ServiceOrder';
import { useRepositoriesContext } from '@/repositories/RepositoriesContext';

import ServiceOrderBillingData from '../ServiceOrderForm/ServiceOrderBillingData';
import usePaymentMethods from '../ServiceOrderForm/usePaymentMethods';
import {
  calculateServiceOrderTotal,
  formatBoolean,
  formatWithSuffix,
  shouldUseInstallationPrice,
} from './serviceOrderShowHelpers';
import useInlineSectionEdit from './useInlineSectionEdit';

/** Labels legibles para los campos del método de pago. */
const PAYMENT_FIELD_LABELS: Record<PaymentMethodField, string> = {
  banco: 'Banco',
  nombre_cuenta: 'Titular',
  numero_cuenta: 'Nº de cuenta',
  numero_de_agencia: 'Número de agencia',
  cbu: 'CBU',
  alias_cbu: 'Alias CBU',
  cuit_cuil: 'CUIT/CUIL',
  clave: 'Clave',
  email_pago: 'Email de pago',
};

interface ServiceOrderShowBillingProps {
  serviceOrder: ServiceOrder;
  blockEdit: boolean;
  refetch: () => Promise<any>;
}

// El Show expande relaciones (currency/payment_method como objetos) pero el Form necesita el id.
const getId = (value: any): number | undefined =>
  value != null && typeof value === 'object' ? value.id : value;
const ServiceOrderShowBilling = (props: ServiceOrderShowBillingProps) => {
  const { t } = useTranslation();
  const { isBrasilSelected } = useCountryContext();
  const { serviceOrderRepository } = useRepositoriesContext();
  const { serviceOrder, blockEdit, refetch } = props;
  const [isEditing, setIsEditing] = useState(false);
  const [notificationApi, notificationContextHolder] = useNotification();
  const [modal, contextHolder] = Modal.useModal();
  const [generatingLink, setGeneratingLink] = useState(false);
  const [publicLinkUrl, setPublicLinkUrl] = useState<string | null>(null);
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [refreshingTechnician, setRefreshingTechnician] = useState(false);
  // Instalación (ARG) sin precio por hora y horas cargados: usa precio fijo de
  // instalación en su lugar (no afecta órdenes que ya tienen esos dos campos).
  const useInstallationPrice = shouldUseInstallationPrice(serviceOrder, isBrasilSelected);
  const {
    formProps,
    record,
    startEditing,
    handleFinish,
    currencySelectProps,
    assignTechnicianPrice,
    technicianPaymentMethodsSelectProps,
    createPaymentMethod,
    clientPaymentMethodsSelectProps,
    createClientPaymentMethod,
    canEdit,
    isSaving,
  } = useServiceOrderShowBilling(
    serviceOrder,
    blockEdit,
    refetch,
    isEditing,
    setIsEditing,
    useInstallationPrice,
  );
  const getPaymentFieldLabel = (field: PaymentMethodField) =>
    field === 'cuit_cuil' && isBrasilSelected ? 'CPF/CNPJ' : PAYMENT_FIELD_LABELS[field];
  const billingTotal = useMemo(() => calculateServiceOrderTotal(serviceOrder), [serviceOrder]);
  const doGenerateLink = async () => {
    setGeneratingLink(true);
    try {
      const url = await serviceOrderRepository.generatePublicLink(serviceOrder.id);

      setPublicLinkUrl(url);
      setLinkModalOpen(true);
      void refetch?.();
    } catch {
      notificationApi.error({ message: t('Error al generar el link') });
    } finally {
      setGeneratingLink(false);
    }
  };

  const handleGenerateLinkClick = () => {
    if (serviceOrder.public_token) {
      modal.confirm({
        title: t('Esto invalidará el link anterior. ¿Continuar?'),
        onOk: doGenerateLink,
      });
    } else {
      void doGenerateLink();
    }
  };

  const handleRefreshTechnician = async () => {
    setRefreshingTechnician(true);
    try {
      await refetch?.();
    } finally {
      setRefreshingTechnician(false);
    }
  };

  // Número del técnico solo con dígitos para el link de WhatsApp (wa.me no admite + ni espacios)
  const technicianPhone = (serviceOrder.technician as unknown as { tel?: string } | undefined)?.tel;
  const technicianWhatsappNumber = technicianPhone?.replace(/\D/g, '');
  const technicianId = (serviceOrder.technician as unknown as { id?: number } | undefined)?.id;
  // Datos para el mensaje de WhatsApp (los valores dinámicos se resaltan en negrita con *asteriscos*)
  const technicianName =
    (serviceOrder.technician as unknown as { nombre?: string } | undefined)?.nombre ?? '';
  const companyName =
    (serviceOrder.company as unknown as { full_name?: string } | undefined)?.full_name ?? '';
  const serviceTypeLabel = t(ServiceOrderTypeString[serviceOrder.type] ?? '');
  const serviceDateLabel = serviceOrder.service_date
    ? dayjs(serviceOrder.service_date).format('LL')
    : '';
  const machineList = (
    serviceOrder.moviles as unknown as { id: number; description: string }[] | undefined
  )
    ?.map(movil => movil.description)
    .filter(Boolean);
  const machineNames = machineList?.join(', ');
  const bold = (value: string) => `*${value}*`;
  // Arma el mensaje de WhatsApp para el técnico. El fragmento de la máquina solo se incluye si existe.
  const buildTechnicianWhatsappMessage = () => {
    let machineFragment = '';

    if (machineNames) {
      machineFragment =
        machineList && machineList.length > 1
          ? t(' en las máquinas {{machine}}', {
              machine: bold(machineNames),
              interpolation: { escapeValue: false },
            })
          : t(' en la máquina {{machine}}', {
              machine: bold(machineNames),
              interpolation: { escapeValue: false },
            });
    }

    const message = t(
      'Hola {{name}}, te comparto los detalles de tu {{type}} del día {{date}}{{machine}} para la empresa {{company}}. Por favor confirmá los datos. ¡Gracias!',
      {
        name: bold(technicianName),
        type: bold(serviceTypeLabel),
        date: bold(serviceDateLabel),
        machine: machineFragment,
        company: bold(companyName),
        interpolation: { escapeValue: false },
      },
    );

    return `${message}\n\n${publicLinkUrl ?? ''}`;
  };

  const editButton = canEdit ? (
    <Button size="small" icon={<EditOutlined />} onClick={startEditing}>
      {t('Editar')}
    </Button>
  ) : undefined;
  // La conformidad del técnico por link no aplica en Brasil.
  const linkButton =
    canEdit && !isBrasilSelected ? (
      <Button
        size="small"
        icon={<LinkOutlined />}
        loading={generatingLink}
        onClick={handleGenerateLinkClick}>
        {serviceOrder.public_token
          ? t('Regenerar link para técnico')
          : t('Generar link para técnico')}
      </Button>
    ) : undefined;
  const headerExtra = (
    <Flex gap={8} wrap="wrap" justify="end">
      {editButton}
      {linkButton}
    </Flex>
  );

  if (isEditing) {
    return (
      <ShowSection
        title={t('Detalles de la facturación')}
        className="ServiceOrderShowBilling"
        icon={<DollarOutlined />}>
        <Form {...formProps} initialValues={record} onFinish={handleFinish} layout="vertical">
          <ServiceOrderBillingData
            formProps={formProps}
            currencySelectProps={currencySelectProps}
            assignTechnicianPrice={assignTechnicianPrice}
            paymentMethodSelectProps={technicianPaymentMethodsSelectProps}
            onCreatePaymentMethod={createPaymentMethod}
            clientPaymentMethodSelectProps={clientPaymentMethodsSelectProps}
            onCreateClientPaymentMethod={createClientPaymentMethod}
            useInstallationPrice={useInstallationPrice}
          />
          <ModalFormActions onCancel={() => setIsEditing(false)} />
        </Form>
        {contextHolder}
        {notificationContextHolder}
      </ShowSection>
    );
  }

  return (
    <ShowSection
      title={t('Detalles de la facturación')}
      className="ServiceOrderShowBilling"
      headerExtra={headerExtra}
      icon={<DollarOutlined />}>
      {isSaving ? (
        <Skeleton active />
      ) : (
        <>
          <ShowSubsection title={t('Tarifas')}>
            <ShowInfoGrid>
              {serviceOrder.installation_price != null ? (
                <ShowInfoItem label={t('Precio de la instalación')}>
                  <TextField
                    value={formatWithSuffix(
                      serviceOrder.installation_price,
                      serviceOrder.currency?.simbolo ?? '',
                    )}
                  />
                </ShowInfoItem>
              ) : (
                <ShowInfoItem label={t('Precio por hora')}>
                  <TextField
                    value={formatWithSuffix(
                      serviceOrder.hourly_rate,
                      `${serviceOrder.currency?.simbolo}/hs`,
                    )}
                  />
                </ShowInfoItem>
              )}
              <ShowInfoItem label={t('Precio por kilómetro')}>
                <TextField
                  value={formatWithSuffix(
                    serviceOrder.kilometres_rate,
                    `${serviceOrder.currency?.simbolo}/km`,
                  )}
                />
              </ShowInfoItem>
            </ShowInfoGrid>
          </ShowSubsection>

          <ShowSubsection title={t('Horas y kilómetros')}>
            <ShowInfoGrid>
              {serviceOrder.installation_price == null && (
                <ShowInfoItem label={t('Horas')}>
                  <TextField value={formatWithSuffix(serviceOrder.hours_estimated, 'hs')} />
                </ShowInfoItem>
              )}
              <ShowInfoItem label={t('Kilómetros recorridos')}>
                <TextField value={formatWithSuffix(serviceOrder.kilometres_estimated, 'km')} />
              </ShowInfoItem>
            </ShowInfoGrid>
          </ShowSubsection>

          <ShowSubsection title={t('Opciones de pago')}>
            <ShowInfoGrid>
              <ShowInfoItem label={t('Pagar servicio al técnico')}>
                <TextField value={formatBoolean(serviceOrder.technician_pay, t('Sí'), t('No'))} />
              </ShowInfoItem>
              <ShowInfoItem label={t('Facturar al cliente')}>
                <TextField value={formatBoolean(serviceOrder.customer_invoice, t('Sí'), t('No'))} />
              </ShowInfoItem>
              {serviceOrder.technician_pay && serviceOrder.payment_method && (
                <ShowInfoItem label={t('Método de pago del técnico')}>
                  <div>
                    <TextField
                      value={t(PaymentMethodTypeLabels[serviceOrder.payment_method.tipo])}
                    />
                    {getPaymentMethodFieldsByType(
                      serviceOrder.payment_method.tipo,
                      isBrasilSelected,
                    ).map(field => {
                      const paymentMethodValue = (
                        serviceOrder.payment_method as Record<string, any>
                      )?.[field];

                      if (paymentMethodValue == null || paymentMethodValue === '') return null;

                      return (
                        <div key={field}>
                          <Typography.Text type="secondary">
                            {t(getPaymentFieldLabel(field))}:{' '}
                          </Typography.Text>
                          <Typography.Text>{paymentMethodValue}</Typography.Text>
                        </div>
                      );
                    })}
                  </div>
                </ShowInfoItem>
              )}
              {serviceOrder.customer_invoice && serviceOrder.payment_method_client && (
                <ShowInfoItem label={t('Método de cobro al cliente')}>
                  <div>
                    <TextField
                      value={t(PaymentMethodTypeLabels[serviceOrder.payment_method_client.tipo])}
                    />
                    {getPaymentMethodFieldsByType(
                      serviceOrder.payment_method_client.tipo,
                      isBrasilSelected,
                    ).map(field => {
                      const paymentMethodValue = (
                        serviceOrder.payment_method_client as Record<string, any>
                      )?.[field];

                      if (paymentMethodValue == null || paymentMethodValue === '') return null;

                      return (
                        <div key={field}>
                          <Typography.Text type="secondary">
                            {t(getPaymentFieldLabel(field))}:{' '}
                          </Typography.Text>
                          <Typography.Text>{paymentMethodValue}</Typography.Text>
                        </div>
                      );
                    })}
                  </div>
                </ShowInfoItem>
              )}
            </ShowInfoGrid>
          </ShowSubsection>

          <ShowSubsection title={t('Cubierto por Acronex')}>
            <ShowInfoGrid>
              <ShowInfoItem label={t('Horas cubiertas por Acronex')}>
                <TextField value={formatWithSuffix(serviceOrder.hours_covered, 'hs')} />
              </ShowInfoItem>
              <ShowInfoItem label={t('Kilómetros cubiertos por Acronex')}>
                <TextField value={formatWithSuffix(serviceOrder.kilometres_covered, 'km')} />
              </ShowInfoItem>
              <ShowInfoItem label={t('Comentarios / Gastos adicionales')} colSpan={2}>
                <TextField value={serviceOrder.aditional_comment ?? '-'} />
              </ShowInfoItem>
            </ShowInfoGrid>
          </ShowSubsection>

          {/* Brasil no tiene paso de administración: el total se muestra acá una vez
              cargados los datos de facturación y la Nota Fiscal. */}
          {isBrasilSelected &&
            billingTotal !== undefined &&
            serviceOrder.currency !== undefined &&
            serviceOrder.bill_file && (
              <ShowInfoItem
                label={`${t('TOTAL POR HORAS Y KILÓMETROS')} (${t('sin IVA')})`}
                className="ServiceOrderShowBillingTotal">
                <TextField value={formatWithSuffix(billingTotal, serviceOrder.currency.simbolo)} />
              </ShowInfoItem>
            )}
        </>
      )}
      <Modal
        open={linkModalOpen}
        title={t('Link para técnico')}
        footer={null}
        onCancel={() => setLinkModalOpen(false)}>
        <Flex vertical gap={12}>
          <Alert
            type="info"
            showIcon
            message={t(
              'Cuando el técnico confirme los datos, la orden pasará automáticamente a "Pendiente de facturación".',
            )}
          />
          <Input readOnly value={publicLinkUrl ?? ''} />
          <Flex gap={8}>
            <Button
              type="primary"
              icon={<CopyOutlined />}
              onClick={() => {
                void navigator.clipboard.writeText(publicLinkUrl ?? '').then(() => {
                  notificationApi.success({ message: t('Link copiado') });
                });
              }}>
              {t('Copiar')}
            </Button>
            <Button
              icon={<WhatsAppOutlined />}
              disabled={!technicianWhatsappNumber}
              onClick={() => {
                window.open(
                  `https://wa.me/${technicianWhatsappNumber}?text=${encodeURIComponent(
                    buildTechnicianWhatsappMessage(),
                  )}`,
                  '_blank',
                );
              }}>
              {t('Enviar')}
            </Button>
          </Flex>
          {!technicianWhatsappNumber && (
            <Alert
              type="info"
              showIcon
              message={
                <span>
                  {t('El técnico')}{' '}
                  <LinkTextField
                    admin
                    to={`${TECHNICIAN_URLS.ADMIN}/${technicianId}`}
                    label={technicianName || '-'}
                  />{' '}
                  {t(
                    'no tiene un número de teléfono cargado. Completá sus datos para poder enviarle la confirmación por WhatsApp.',
                  )}
                </span>
              }
              description={
                <Button
                  size="small"
                  icon={<ReloadOutlined />}
                  loading={refreshingTechnician}
                  onClick={() => void handleRefreshTechnician()}>
                  {t('Ya agregué el número de teléfono')}
                </Button>
              }
            />
          )}
        </Flex>
      </Modal>
      {contextHolder}
      {notificationContextHolder}
    </ShowSection>
  );
};

const useServiceOrderShowBilling = (
  serviceOrder,
  blockEdit,
  refetch,
  isEditing,
  setIsEditing,
  useInstallationPrice: boolean,
) => {
  const { t } = useTranslation();
  const { hasPermission } = usePermissionContext();
  const record = useMemo(
    () => ({
      ...serviceOrder,
      currency: getId(serviceOrder.currency),
      payment_method: getId(serviceOrder.payment_method),
      payment_method_client: getId(serviceOrder.payment_method_client),
      hours_covered: serviceOrder.hours_covered ?? 0,
      kilometres_covered: serviceOrder.kilometres_covered ?? 0,
    }),
    [serviceOrder],
  );
  const { formProps, isSaving, startEditing, handleFinish } = useInlineSectionEdit({
    id: serviceOrder.id,
    record,
    isEditing,
    setIsEditing,
    refetch,
    successMessage: t('Orden de servicio actualizada correctamente'),
  });
  const companyId = getId(serviceOrder.company);
  const technician = serviceOrder.technician as Technician;
  // El endpoint de currencies no soporta filtros: se trae la lista completa y la
  // moneda USD (fija en Instalación) se busca del lado del cliente por abreviación.
  const { selectProps: currencySelectProps } = useSelectScrollSearch(
    {
      resource: 'currencies',
      optionLabel: (item: any) => item.nombre + ' (' + item.abreviacion + ')',
      pagination: { mode: 'off', pageSize: 3000 },
      queryOptions: { enabled: isEditing },
    },
    { saveItem: true },
  );
  const {
    technicianPaymentMethodsSelectProps,
    createPaymentMethod,
    clientPaymentMethodsSelectProps,
    createClientPaymentMethod,
  } = usePaymentMethods(formProps.form, technician ?? null, companyId);
  const assignTechnicianPrice = useMemo(() => {
    if (!technician?.moneda) {
      return undefined;
    }

    const {
      external_hour_price,
      external_km_price,
      internal_hour_price,
      internal_km_price,
      moneda,
    } = technician;

    if (serviceOrder.type === SERVICE_ORDER_TYPE.INSTALACION) {
      // El precio fijo de instalación lo escribe el usuario, no se autocompleta.
      return (internal_hour_price || internal_km_price) && !useInstallationPrice
        ? () =>
            formProps.form?.setFieldsValue({
              currency: moneda.id,
              hourly_rate: internal_hour_price,
              kilometres_rate: internal_km_price,
            })
        : undefined;
    }

    return external_hour_price || external_km_price
      ? () =>
          formProps.form?.setFieldsValue({
            currency: moneda.id,
            hourly_rate: external_hour_price,
            kilometres_rate: external_km_price,
          })
      : undefined;
  }, [technician, serviceOrder.type, formProps.form, useInstallationPrice]);
  const canEdit =
    !blockEdit &&
    !isEditing &&
    !isSaving &&
    hasPermission('edit') &&
    serviceOrder.status === SERVICE_ORDER_STATUS.REALIZADA;

  return {
    formProps,
    record,
    startEditing,
    handleFinish,
    currencySelectProps,
    assignTechnicianPrice,
    technicianPaymentMethodsSelectProps,
    createPaymentMethod,
    clientPaymentMethodsSelectProps,
    createClientPaymentMethod,
    canEdit,
    isSaving,
  };
};

export default ServiceOrderShowBilling;
